// Marriage manager — player-to-player relationships. Distinct from the
// LineageConfig "Royal Pairing" goon-breeding mechanic in populationManager.
//
// Lifecycle:
//   propose → MarriageProposalSent (target receives a pending proposal)
//   accept  → MarriageAccepted (both profiles record an active marriage)
//   divorce → MarriageDivorced (emperor decrees a random split per case)
//
// Cross-user proposals require backend routing (accountManager + a real
// server endpoint). For now, when the target is an NPC roster entry, the
// proposal is auto-accepted on the next tick so the UX is testable
// end-to-end. Real account targets pend until the other client accepts.

import { Events, emit, on } from '../ui/eventBus.js'
import { MarriageRules, DivorceConfig, KingdomEmperors, BirthConfig, LicenseTypes } from '../config/balance.js'
import { getProfile, persist, activeId, active } from './state.js'
import { pushLog } from '../ui/hud.js'
import { formatCoins } from '../ui/format.js'
import { hasLicense } from './profileManager.js'

export function init() {
  on('marriage:propose', ({ target }) => propose(target))
  on('marriage:accept', ({ proposalId }) => accept(proposalId))
  on('marriage:decline', ({ proposalId }) => decline(proposalId))
  on('marriage:divorce', ({ marriageId }) => divorce(marriageId))
  on('marriage:toggleOpen', () => toggleOpenToMarriage())
}

// Direct exports so the Registry panel can call without going through the
// bus — useful both as belt-and-suspenders against any HMR-mismatched bus
// instances, and to keep panel code typed/discoverable.
export { propose, accept, decline, divorce, toggleOpenToMarriage, haveChild }

// Why birth might fail right now (label-friendly reason for the UI). Returns
// `null` when the requested marriage IS ready for a new birth.
export function birthBlockReason(marriageId) {
  const p = ensureSchema()
  const m = p.marriages.find((x) => x.id === marriageId && x.status === 'ACTIVE')
  if (!m) return 'No active marriage selected.'
  if (!hasLicense(LicenseTypes.BIRTH)) return 'Need Birth License.'
  const k = active()
  if (k.wealth < BirthConfig.cost) return `Need ${formatCoins(BirthConfig.cost)}.`
  const since = Date.now() - (m.lastBirthAt ?? 0)
  if (since < BirthConfig.cooldownMs) {
    const left = Math.ceil((BirthConfig.cooldownMs - since) / 1000)
    return `Cooldown: ${left}s.`
  }
  return null
}

function haveChild(marriageId) {
  const reason = birthBlockReason(marriageId)
  if (reason) {
    pushLog(`Cannot bear a child — ${reason.toLowerCase()}`)
    return false
  }
  const p = ensureSchema()
  const k = active()
  const m = p.marriages.find((x) => x.id === marriageId && x.status === 'ACTIVE')
  k.wealth -= BirthConfig.cost
  emit(Events.WealthChange, { wealth: k.wealth, delta: -BirthConfig.cost })

  const name = BirthConfig.namePool[Math.floor(Math.random() * BirthConfig.namePool.length)]
  const child = {
    id: `child_${Date.now()}_${Math.random().toString(36).slice(2, 7)}`,
    name,
    parentMarriageId: marriageId,
    partner: { name: m.partner?.name ?? 'Unknown', kingdom: m.partner?.kingdom ?? '' },
    kingdom: activeId(),
    bornAt: Date.now(),
  }
  p.children.push(child)
  m.lastBirthAt = Date.now()
  persist()
  pushLog(`A kitten is born: ${child.name}, child of you and ${child.partner.name}.`)
  emit(Events.ChildBorn, { child, marriageId })
  return true
}

// Ensure the marriage-related arrays exist on the profile — pre-Phase 11
// saves predate these fields, so every mutator calls this first to migrate
// the loaded profile in place.
function ensureSchema() {
  const p = getProfile()
  if (!Array.isArray(p.marriages)) p.marriages = []
  if (!Array.isArray(p.marriageProposalsIn)) p.marriageProposalsIn = []
  if (!Array.isArray(p.marriageProposalsOut)) p.marriageProposalsOut = []
  if (!Array.isArray(p.children)) p.children = []
  return p
}

// Rule summary helpers — used by both the manager and the UI to surface
// whether the player can take another spouse.
export function activeMarriages() {
  return (getProfile().marriages ?? []).filter((m) => m.status === 'ACTIVE')
}

export function isAcceptorOnAny() {
  return activeMarriages().some((m) => m.role === 'acceptor')
}

export function canPropose() {
  // Already accepted into a marriage → locked out of further proposals.
  if (isAcceptorOnAny()) return false
  const asRequester = activeMarriages().filter((m) => m.role === 'requester').length
  return asRequester < MarriageRules.maxAsRequester
}

export function canAccept() {
  // Same rule as propose — acceptors are locked to one partner.
  return canPropose()
}

export function isOpenToMarriage() {
  return !!getProfile().openToMarriage
}

function toggleOpenToMarriage() {
  const p = ensureSchema()
  p.openToMarriage = !p.openToMarriage
  persist()
  emit(Events.OpenToMarriageChange, { open: p.openToMarriage })
}

// target shape: { id, name, kingdom, isNpc, wealth }
// `id` is unique per partner — for NPCs it's the roster entry id; for real
// accounts it's the remote profile id once accountManager is wired.
function propose(target) {
  if (!canPropose()) return false
  const p = ensureSchema()
  const proposalId = `prop_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`
  const proposal = {
    id: proposalId,
    target,
    sentAt: Date.now(),
    from: { name: p.catName, kingdom: activeId() },
  }
  p.marriageProposalsOut.push(proposal)
  persist()
  emit(Events.MarriageProposalSent, { proposal })
  // NPC targets auto-accept after a short beat (simulated network delay)
  // so the player gets visible feedback even in single-player mode.
  if (target.isNpc) {
    setTimeout(() => acceptIncoming(buildIncomingFromOutgoing(proposalId)), 600)
  }
  return true
}

function buildIncomingFromOutgoing(proposalId) {
  const p = ensureSchema()
  const out = p.marriageProposalsOut.find((x) => x.id === proposalId)
  if (!out) return null
  // Pretend the NPC sent us their own incoming proposal — shape mirrors the
  // structure other real users would push to us via backend.
  return {
    id: out.id,
    from: out.target,
    target: out.from,
    sentAt: out.sentAt,
  }
}

// Called when the OTHER party accepts — could come from backend webhook or,
// for NPCs, from the buildIncomingFromOutgoing fast-path above.
function acceptIncoming(incoming) {
  if (!incoming) return
  const p = ensureSchema()
  const out = p.marriageProposalsOut.find((x) => x.id === incoming.id)
  if (!out) return
  p.marriageProposalsOut = p.marriageProposalsOut.filter((x) => x.id !== incoming.id)
  p.marriages.push({
    id: `mar_${incoming.id}`,
    partner: out.target,
    role: 'requester',
    marriedAt: Date.now(),
    status: 'ACTIVE',
  })
  persist()
  emit(Events.MarriageAccepted, { partner: out.target, role: 'requester' })
}

// Called when WE accept a proposal that came in from another player.
function accept(proposalId) {
  if (!canAccept()) return false
  const p = ensureSchema()
  const incoming = p.marriageProposalsIn.find((x) => x.id === proposalId)
  if (!incoming) return false
  p.marriageProposalsIn = p.marriageProposalsIn.filter((x) => x.id !== proposalId)
  p.marriages.push({
    id: `mar_${proposalId}`,
    partner: incoming.from,
    role: 'acceptor',
    marriedAt: Date.now(),
    status: 'ACTIVE',
  })
  persist()
  emit(Events.MarriageAccepted, { partner: incoming.from, role: 'acceptor' })
  return true
}

function decline(proposalId) {
  const p = ensureSchema()
  p.marriageProposalsIn = p.marriageProposalsIn.filter((x) => x.id !== proposalId)
  persist()
}

// Emperor randomly decrees one of the configured splits per case and
// ENFORCES the settlement: combined wealth (self + partner) is divided per
// the decree, both parties get notified, and the divorce is permanently
// recorded on the marriage entry (which the Registry shows under its
// "MARRIAGE" section even after status flips to DIVORCED).
function divorce(marriageId) {
  const p = ensureSchema()
  const k = active()
  const marriage = p.marriages.find((m) => m.id === marriageId && m.status === 'ACTIVE')
  if (!marriage) return false

  const splits = DivorceConfig.splits
  const pick = splits[Math.floor(Math.random() * splits.length)]
  const requesterPct = pick.requesterPct
  const partnerWealth = marriage.partner?.wealth ?? 0
  const combined = k.wealth + partnerWealth
  const selfRole = marriage.role // 'requester' | 'acceptor'
  const selfPct = selfRole === 'requester' ? requesterPct : 1 - requesterPct
  const selfShare = Math.round(combined * selfPct)
  const partnerShare = combined - selfShare
  const selfDelta = selfShare - k.wealth

  // Enforce the settlement on our own ledger immediately. Partner's "wealth"
  // on the marriage record updates so the Registry shows the new totals; in
  // production this would also push to the other user's account.
  k.wealth = selfShare
  emit(Events.WealthChange, { wealth: k.wealth, delta: selfDelta })

  marriage.status = 'DIVORCED'
  marriage.divorcedAt = Date.now()
  marriage.divorceSplit = pick
  marriage.settlement = { selfShare, partnerShare, selfPct, requesterPct, combined }
  // Mutate the snapshot of partner wealth on the marriage record so future
  // re-marriages or registry views reflect the post-settlement reality.
  if (marriage.partner) marriage.partner.wealth = partnerShare
  persist()

  const emperor = KingdomEmperors[activeId()] ?? KingdomEmperors.WEI
  const decree = emperor.divorce?.(requesterPct, requesterPct)
    ?? `Decree: ${Math.round(requesterPct * 100)}% to the requester.`

  // Both parties get notified. The partner notification is logged locally
  // for NPCs; for real users this would round-trip via accountManager.
  const partnerName = marriage.partner?.name ?? 'your spouse'
  pushLog(`Divorce filed. The Emperor decrees ${pick.label}. You receive ${formatCoins(selfShare)}; ${partnerName} receives ${formatCoins(partnerShare)}.`)
  pushLog(`Notice sent to ${partnerName} (and to ${emperor.name}'s court).`)

  emit(Events.MarriageDivorced, { marriage, split: pick, settlement: marriage.settlement })
  emit(Events.NarrativeBeat, {
    title: `${emperor.name} ${emperor.cn} — Divorce Decree (${pick.label})`,
    body:
      `${decree}\n\n` +
      `Combined estate: ${formatCoins(combined)}.\n` +
      `Your share: ${formatCoins(selfShare)}.\n` +
      `${partnerName}: ${formatCoins(partnerShare)}.`,
    kingdom: activeId(),
  })
  return true
}

// Combined wealth display for the Registry. Real cross-account totals will
// come from backend; for now we surface only known partners' wealth (NPC
// roster entries carry their wealth field).
export function combinedWealth(selfWealth) {
  let total = selfWealth
  for (const m of activeMarriages()) {
    total += m.partner?.wealth ?? 0
  }
  return total
}
